import { ICellRendererParams } from '@ag-grid-community/core'
import { Progress, Tag } from '@surecompanies/core_components'
import { ContributionReportJobsStatus } from 'types/contribution'
import { ContributionReportResponseOneDto } from 'modules/contribution-report-service/dtos'
import { useContributionReport } from 'modules/contribution-report-service/hooks/useContributionReport'
import React from 'react'

export default function ContributionReportProgressCell({
  data,
}: ICellRendererParams<ContributionReportResponseOneDto>) {
  const { getContributionReport } = useContributionReport()

  const shouldPollStatus = (status?: ContributionReportJobsStatus): boolean => {
    if (!status) return false
    return (
      status === ContributionReportJobsStatus.CREATED ||
      status === ContributionReportJobsStatus.PROCESSING ||
      status === ContributionReportJobsStatus.AGGREGATING
    )
  }

  // Determine if we should poll based on current status
  const shouldPoll = !!data?.id && shouldPollStatus(data.status)

  // Poll for updates if the report is still in progress
  const { data: reportData } = getContributionReport(data?.id ?? '', {
    queryOptions: {
      enabled: shouldPoll, // Only enable query if status requires polling
      refetchInterval: (query) => {
        // Check the latest status from the query data
        const latestStatus = query.state.data?.status ?? data?.status
        // Reuse the same polling condition
        return shouldPollStatus(latestStatus) ? 5000 : false
      },
    } as any, // Type workaround - queryKey is set internally by the hook
  })

  // Use the latest data (either from polling or from the row data)
  const currentData = reportData ?? data

  if (!currentData) {
    return <span>-</span>
  }

  const { status, progress, completedWorkerJobs, totalWorkerJobs } = currentData

  // Convert progress from decimal (0-1) to percentage (0-100)
  const progressPercent =
    progress !== undefined ? Math.round(progress * 100) : undefined

  // Determine status color
  const getStatusColor = (status: ContributionReportJobsStatus) => {
    switch (status) {
      case ContributionReportJobsStatus.COMPLETED:
        return 'success'
      case ContributionReportJobsStatus.FAILED:
        return 'error'
      case ContributionReportJobsStatus.CANCELLED:
        return 'default'
      case ContributionReportJobsStatus.PROCESSING:
      case ContributionReportJobsStatus.AGGREGATING:
        return 'processing'
      default:
        return 'default'
    }
  }

  // Format status text
  const getStatusText = (status: ContributionReportJobsStatus) => {
    switch (status) {
      case ContributionReportJobsStatus.CREATED:
        return 'Created'
      case ContributionReportJobsStatus.PROCESSING:
        return 'Processing'
      case ContributionReportJobsStatus.AGGREGATING:
        return 'Aggregating'
      case ContributionReportJobsStatus.COMPLETED:
        return 'Completed'
      case ContributionReportJobsStatus.FAILED:
        return 'Failed'
      case ContributionReportJobsStatus.CANCELLED:
        return 'Cancelled'
      default:
        return status
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Tag
          color={getStatusColor(status)}
          style={{ width: '80px', textAlign: 'center' }}
        >
          {getStatusText(status)}
        </Tag>
        {progressPercent !== undefined && (
          <span style={{ fontSize: '12px', color: '#666' }}>
            {progressPercent}%
          </span>
        )}
      </div>
      {progressPercent !== undefined && (
        <div style={{ width: '100%', maxWidth: '200px' }}>
          <Progress
            percent={progressPercent}
            size="small"
            showInfo={false}
            strokeWidth={6}
            status={
              status === ContributionReportJobsStatus.FAILED
                ? 'exception'
                : status === ContributionReportJobsStatus.COMPLETED
                  ? 'success'
                  : 'active'
            }
          />
        </div>
      )}
      {totalWorkerJobs !== undefined && totalWorkerJobs > 0 && (
        <div style={{ fontSize: '12px', color: '#666' }}>
          Workers: {completedWorkerJobs ?? 0}/{totalWorkerJobs}
        </div>
      )}
    </div>
  )
}
